import { useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

// Monthly plans are set up with our team for now: the visitor is sent to the contact form (which
// creates a lead) instead of Stripe. Set REACT_APP_ONLINE_SUBSCRIPTIONS=1 (and PLAN_RECURRING=1 on
// the server) to bring online subscriptions back.
const ONLINE_SUBSCRIPTIONS = process.env.REACT_APP_ONLINE_SUBSCRIPTIONS === "1";
const reachOutUrl = (planId) => `/contact?plan=${encodeURIComponent(planId || "")}`;

/**
 * useStripeCheckout
 *
 * Calls /api/checkout/create-session on the backend and redirects the user
 * to Stripe's hosted checkout page.
 *
 * Usage:
 *   const { startCheckout, loading, error } = useStripeCheckout();
 *   await startCheckout({ planId: "starter-299", auditId: "abc123" });
 *
 * Plan IDs (must match backend _STRIPE_PLANS keys):
 *   "starter-299"   — $299/month  (site-wide PricingPage)
 *   "starter-899"   — $899/month  (AuditPackagePage, Starter tier)
 *   "growth-2499"   — $2499/month (AuditPackagePage, Growth tier)
 */
export function useStripeCheckout() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function startCheckout({
    planId,
    auditId = null,
    customerEmail = null,
    successUrl: successUrlOverride = null,
    cancelUrl: cancelUrlOverride = null,
  }) {
    if (!ONLINE_SUBSCRIPTIONS) {
      window.location.href = reachOutUrl(planId);
      return;
    }
    setLoading(true);
    setError(null);

    const successUrl = successUrlOverride || (auditId
      ? `${window.location.origin}/audit-package?audit_id=${auditId}`
      : `${window.location.origin}/pricing`);

    const cancelUrl = cancelUrlOverride || window.location.href;

    try {
      const res = await fetch(`${API}/api/checkout/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_id: planId,
          audit_id: auditId,
          success_url: successUrl,
          cancel_url: cancelUrl,
          customer_email: customerEmail,
        }),
      });

      if (res.status === 409) {
        // The server says this plan is set up with our team.
        window.location.href = reachOutUrl(planId);
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Server error ${res.status}`);
      }

      const { checkout_url } = await res.json();
      // Redirect to Stripe hosted checkout
      window.location.href = checkout_url;
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  return { startCheckout, loading, error };
}
